/* `Action.logs` → one list of rows, whatever shape the server actually sent.
 *
 * There are FIVE shapes in production and the old page handled one and a half of them. Reading
 * them from picsell-api rather than guessing:
 *
 * 1. `{invalid_tasks: [{title, description, start, end, point, network, images, validation_error}]}`
 *    — the point path (`tasks/services/task_services.py`). `validation_error` is a DICT, either
 *    `{field: [messages]}` from a Django ValidationError or the catch-all `{error: "<str>"}`.
 * 2. `{invalid_tasks: ["title", "description", "start", …]}` — the NETWORK path. The worker writes
 *    `logs = {'invalid_tasks': {…a dict…}}` (`tasks/tasks.py`), and `ActionSerializer.get_logs`
 *    then does `list += dict`, which iterates the dict's KEYS. So a failed network import
 *    serialises as an array of field-name strings and every `.title` on it is `undefined`. It also
 *    records only the first failure regardless of how many points failed.
 * 3. `{invalid_matrices: [{point, products: [], validation_error: "<str>"}]}` — here
 *    `validation_error` is a plain STRING, not the dict it is on the task side.
 * 4. `{fail_reason: "<str>"}` — written straight onto the Action when the fan-out itself throws
 *    (`points/api/web/v1/views.py`). Undocumented and previously unhandled.
 * 5. `{}` — success, and also "this action spawned no DelayedTask rows at all". The two are
 *    indistinguishable through the API.
 *
 * Shape 2 is lossy at the source: the record is gone before it reaches us, so it is reported as
 * such rather than rendered as a table of `undefined`s.
 */

/** `validation_error` in either of its two forms, as one line of text. */
export function reasonText(error) {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (typeof error !== "object") return String(error);

  /* The catch-all wrapper carries nothing but the message. */
  if (typeof error.error === "string" && Object.keys(error).length === 1) return error.error;

  return Object.entries(error)
    .map(([field, messages]) => `${field}: ${[].concat(messages).join(", ")}`)
    .join("; ");
}

/**
 * @returns {{kind: "tasks"|"matrices"|"message"|"opaque"|"none", rows: Array}}
 *   `opaque` means the server recorded a failure but not which records — shape 2 above.
 */
export function readLogs(action) {
  const logs = action?.logs;
  if (!logs || typeof logs !== "object" || Array.isArray(logs)) return { kind: "none", rows: [] };

  if (typeof logs.fail_reason === "string" && logs.fail_reason.trim()) {
    return { kind: "message", rows: [{ key: "fail", reason: logs.fail_reason }] };
  }

  if (logs.invalid_tasks) {
    const list = Array.isArray(logs.invalid_tasks) ? logs.invalid_tasks : [logs.invalid_tasks];
    const records = list.filter(item => item && typeof item === "object" && !Array.isArray(item));
    if (!records.length) return { kind: list.length ? "opaque" : "none", rows: [] };

    return {
      kind: "tasks",
      rows: records.map((item, index) => ({
        key: `t${index}`,
        title: item.title || "",
        description: item.description || "",
        /* `str()` of whatever arrived, so these are not reliably dates — shown verbatim. */
        start: item.start || "",
        end: item.end || "",
        point: item.point || "",
        network: item.network || "",
        reason: reasonText(item.validation_error),
      })),
    };
  }

  if (logs.invalid_matrices) {
    const list = Array.isArray(logs.invalid_matrices) ? logs.invalid_matrices : [logs.invalid_matrices];
    const records = list.filter(item => item && typeof item === "object" && !Array.isArray(item));
    if (!records.length) return { kind: list.length ? "opaque" : "none", rows: [] };

    return {
      kind: "matrices",
      rows: records.map((item, index) => ({
        key: `m${index}`,
        point: item.point || "",
        products: Array.isArray(item.products) ? item.products : [],
        reason: reasonText(item.validation_error),
      })),
    };
  }

  return { kind: "none", rows: [] };
}

/** Whether the row has anything to show — drives the log link and the drawer. */
export const hasLogs = action => readLogs(action).kind !== "none";

/**
 * The rows as a CSV table, for the export. `t` translates a column key.
 *
 * The page this replaces built this string with `join(",")` and a hardcoded header whose order did
 * not match the values it then wrote — `title,start,end,description` against
 * `title, start, end, description` interpolated as `title, start, end, description` in a different
 * order — so the exported Start column held descriptions.
 */
export function logsToCsv(action, t) {
  const { kind, rows } = readLogs(action);

  if (kind === "tasks") {
    return [
      [t("col_title"), t("col_description"), t("col_start"), t("col_end"), t("col_point"), t("col_network"), t("col_reason")],
      ...rows.map(row => [row.title, row.description, row.start, row.end, row.point, row.network, row.reason]),
    ];
  }
  if (kind === "matrices") {
    return [[t("col_point"), t("col_products"), t("col_reason")], ...rows.map(row => [row.point, row.products.join(" "), row.reason])];
  }
  if (kind === "message") return [[t("col_reason")], [rows[0].reason]];
  return [];
}
